// Реестр событий аналитики v2 (MOB-470) — единственное место, где заводится
// событие. Контракт — эпик ANALYTICS-CONTRACT (anyclass-main, docs/epics).
// Новое событие = правка этого файла через ревью. Старые события — в
// registry.legacy.ts, они отправляются по-старому, через Analytics.send.
import {
  defineRegistry,
  InferParams,
  optional,
  optionalEnum,
  required,
  requiredEnum
} from '@web/services/Analytics/v2/params'
import { LEGACY_EVENTS } from '@web/services/Analytics/registry.legacy'

export const TRACKING_VERSION = 2

// Словарь экранов эпика плюс `other` — для экранов, которых в словаре нет
// (авторизация, настройки и т. п.). Словарь ведёт web. В web course_page — лендинг
// курса, экран курса с календарём — calendar; `course` и `program` в web не
// используются, остаются для приложения (вопрос 14 спеки, решено 07.10).
export const PAGE_TYPES = [
  'home', 'catalog', 'course', 'course_page', 'course_day', 'calendar',
  'subscriptions', 'article', 'club_feed', 'search', 'support', 'program', 'other'
] as const

export type PageType = typeof PAGE_TYPES[number]

// Словарь мест (эпик, слой 2: placement) — `<экран>_<слот>`, без платформы:
// desktop / mobile GA4 знает сам. Только для событий v2; старые события шлют
// позиции как раньше (решение 07.10.2026). Построен по BannerMapper.ts
// (Position × Placement) и спецблокам главной. Порядковый номер под категорией
// едет параметром position, а не в имени места.
export const PLACEMENTS = [
  'home_top', 'home_under_categories', 'home_under_category', 'home_sidebar',
  'home_catalog', 'home_new_level',
  'courses_top', 'courses_bottom', 'courses_sidebar', 'courses_before_course', 'courses_catalog',
  'calendar_top', 'calendar_bottom', 'calendar_sidebar', 'calendar_before_stage',
  'course_day_top', 'course_day_bottom', 'course_day_before_lessons',
  'course_day_instead_video', 'course_day_after_video',
  'homeworks_top', 'homeworks_bottom', 'homeworks_sidebar', 'homeworks_catalog',
  'videoconsultations_top', 'videoconsultations_bottom', 'videoconsultations_sidebar',
  'videoconsultations_catalog',
  'support_top', 'support_bottom', 'support_sidebar', 'support_catalog',
  'settings_top', 'settings_bottom', 'settings_sidebar',
  'banners_list',
  // Позиция, которую не удалось разобрать, — сигнал дописать словарь.
  'unknown'
] as const

export type Placement = typeof PLACEMENTS[number]

// Контекст, который track() проставляет сам (§4 контракта, слой 1).
// user_id и type_lk сюда не входят: они уже едут через gtag('set').
export const CONTEXT_PARAMS = {
  event_id: required.string,
  tracking_version: required.number,
  platform: requiredEnum(['web'] as const),
  // ЛК во встроенном режиме: во фрейме или в WebView приложения (?webview=1).
  // В браузере параметра нет. app_version web не знает и не шлёт (вопрос 6).
  embed: optionalEnum(['webview', 'iframe'] as const),
  traffic_type: requiredEnum(['external', 'internal'] as const),
  page_type: requiredEnum(PAGE_TYPES),
  page_path: required.string
} as const

export const REGISTRY = defineRegistry({
  page_view: {
    description: 'Открыт экран: один раз на смену пути в роутере. Экран — page_type из контекста',
    since: '2026-10-12',
    replaces: [
      'main_open', 'load_home', 'course_page_open', 'open_day', 'day_tap', 'first_open_day',
      'load_day', 'open_calendar', 'load_calendar', 'subscription_list_view', 'course_menu',
      'view_feed', 'view_article'
    ],
    params: {
      // Сущность экрана без риска путаницы: id статьи или подписки. У календаря и
      // дня курса object_id нет — шлётся course_id (поток → курс-контент).
      object_id: optional.string,
      // Курс-контент (эпик: два пространства id) — у экранов обучения.
      course_id: optional.string,
      day_number: optional.number,
      // Только в первом page_view сессии (эпик, слой 1).
      utm_source: optional.string,
      utm_medium: optional.string,
      utm_campaign: optional.string,
      utm_content: optional.string,
      utm_term: optional.string
    }
  },
  banner_view: {
    description: 'Баннер показан: один раз на баннер за экран, сколько бы компонентов его ни отслеживали. ' +
      'Дедуп по паре «заход на экран + id баннера»: один баннер в двух местах одного экрана даёт один показ',
    since: '2026-10-12',
    // Старое banner_view и новое называются одинаково — различает tracking_version.
    replaces: ['banner_show', 'banner_view'],
    params: {
      object_id: required.string, // id баннера
      placement: requiredEnum(PLACEMENTS),
      // Порядковый номер, если мест одного вида несколько (под 1-й, 5-й… категорией).
      position: optional.number,
      // Поле type баннера с бэка как есть — словарь значений ведёт бэк
      // (вопрос 20 спеки, решено 12.10). Пустой type не шлём.
      banner_type: optional.string,
      // A/B-тест баннера — id строкой, как object_id; имена как в старых
      // banner_show / banner_click (MOB-606). Баннер не в тесте — полей нет.
      banner_ab_test_id: optional.string,
      banner_ab_test_variant_id: optional.string
    }
  },
  banner_click: {
    description: 'Клик по баннеру: одно событие на клик, сколько бы обработчиков его ни поймали',
    since: '2026-10-12',
    replaces: ['banner_click'],
    params: {
      object_id: required.string,
      placement: requiredEnum(PLACEMENTS),
      position: optional.number,
      banner_type: optional.string,
      banner_ab_test_id: optional.string,
      banner_ab_test_variant_id: optional.string
    }
  }
})

export type EventNameV2 = keyof typeof REGISTRY
export type ParamsOf<N extends EventNameV2> = InferParams<typeof REGISTRY[N]['params']>

// Всё, что нужно DWH для проверки потока, одним JSON-совместимым объектом.
export function exportRegistry () {
  return {
    tracking_version: TRACKING_VERSION,
    context: CONTEXT_PARAMS,
    events: REGISTRY,
    legacy: LEGACY_EVENTS
  }
}
